import {
  bookingTimeUnits,
  getBoundaries,
  getStartOf,
  isDateSameOrAfter, isInRange, isSameDate,
  timestampToDate,
} from '../../util/dates';
import { getTimeSlotsOnDate } from '../../components/OrderPanel/booking.shared';

export const getAvailableStartTimes = params => {
  const {
    intl,
    timeZone,
    bookingStart,
    timeSlotsOnSelectedDate,
    bookingLengthInMinutes,
    startTimeInterval,
  } = params;

  if (timeSlotsOnSelectedDate === undefined) {
    return [];
  }

  if (timeSlotsOnSelectedDate?.length === 0 || !timeSlotsOnSelectedDate[0] || !bookingStart) {
    return [];
  }

  const bookingStartDate = getStartOf(bookingStart, 'day', timeZone);
  const nextDay = getStartOf(bookingStartDate, 'day', timeZone, 1, 'days');
  const timeUnitConfig = bookingTimeUnits[startTimeInterval];
  const overlapWithNextDay = !!timeUnitConfig?.timeUnitInMinutes
    ? bookingLengthInMinutes - timeUnitConfig.timeUnitInMinutes
    : bookingLengthInMinutes;
  const nextDayPlusBookingLength = getStartOf(
    nextDay,
    'minute',
    timeZone,
    overlapWithNextDay,
    'minutes'
  );

  const allStartTimes = timeSlotsOnSelectedDate.reduce((availableStartTimes, t, i) => {
    const startDate = t.attributes.start;
    const lastIndex = findLastAdjacent(i, timeSlotsOnSelectedDate);
    const endDate =
      lastIndex !== i ? timeSlotsOnSelectedDate[lastIndex].attributes.end : t.attributes.end;

    // If the time slot starts before the selected booking start date, use bookingStartDate
    const startLimit = isDateSameOrAfter(bookingStartDate, startDate)
      ? bookingStartDate
      : startDate;

    // If the time slot ends after the next day, use nextDate.
    const endOfTimeSlotOrDay = isDateSameOrAfter(endDate, nextDayPlusBookingLength)
      ? nextDayPlusBookingLength
      : endDate;
    const endLimit = getStartOf(
      endOfTimeSlotOrDay,
      'minute',
      timeZone,
      -1 * bookingLengthInMinutes,
      'minutes'
    );

    const startTimes = getBoundaries(
      startLimit,
      endLimit,
      1,
      timeUnitConfig.timeUnit,
      timeZone,
      intl
    );

    const pickedTimestamps = availableStartTimes.map(t => t.timestamp);
    const uniqueStartTimes = startTimes.filter(t => !pickedTimestamps.includes(t.timestamp));
    return availableStartTimes.concat(uniqueStartTimes);
  }, []);
  return allStartTimes;
};

export const getCustomAvailableStartTimes = params => {
  const {
    intl,
    timeZone,
    bookingStart,
    timeSlotsOnSelectedDate,
    bookingLengthInMinutes,
    startTimeInterval,
  } = params;

  if (timeSlotsOnSelectedDate === undefined) {
    return [];
  }

  if (timeSlotsOnSelectedDate?.length === 0 || !timeSlotsOnSelectedDate[0] || !bookingStart) {
    return [];
  }

  const bookingStartDate = getStartOf(bookingStart, 'day', timeZone);
  const nextDay = getStartOf(bookingStartDate, 'day', timeZone, 1, 'days');
  const timeUnitConfig = bookingTimeUnits[startTimeInterval];
  const overlapWithNextDay = !!timeUnitConfig?.timeUnitInMinutes
    ? bookingLengthInMinutes - timeUnitConfig.timeUnitInMinutes
    : bookingLengthInMinutes;
  const nextDayPlusBookingLength = getStartOf(
    nextDay,
    'minute',
    timeZone,
    overlapWithNextDay,
    'minutes'
  );

  const allStartTimes = timeSlotsOnSelectedDate.reduce((availableStartTimes, t, i) => {
    const startDate = t.attributes.start;
    const lastIndex = findLastAdjacent(i, timeSlotsOnSelectedDate);
    const endDate =
      lastIndex !== i ? timeSlotsOnSelectedDate[lastIndex].attributes.end : t.attributes.end;

    // If the time slot starts before the selected booking start date, use bookingStartDate
    const startLimit = isDateSameOrAfter(bookingStartDate, startDate)
      ? bookingStartDate
      : startDate;

    // If the time slot ends after the next day, use nextDate.
    const endOfTimeSlotOrDay = isDateSameOrAfter(endDate, nextDayPlusBookingLength)
      ? nextDayPlusBookingLength
      : endDate;
    const endLimit = getStartOf(
      endOfTimeSlotOrDay,
      'minute',
      timeZone,
      -1 * bookingLengthInMinutes,
      'minutes'
    );

    const startTimes = getBoundaries(
      startLimit,
      endLimit,
      1,
      timeUnitConfig.timeUnit,
      timeZone,
      intl
    );

    const pickedTimestamps = availableStartTimes.map(t => t.timestamp);
    const uniqueStartTimes = startTimes.filter(t => !pickedTimestamps.includes(t.timestamp));
    return availableStartTimes.concat(uniqueStartTimes);
  }, []);

  return allStartTimes;
};

const getBookingEndTimeAsDate = (bookingStartTime, bookingLengthInMinutes) => {
  const bookingEndTimeAsDate = new Date(
    bookingStartTime.getTime() + bookingLengthInMinutes * 60000
  );
  return bookingEndTimeAsDate;
};

const findLastAdjacent = (index, timeSlots) => {
  const current = timeSlots[index];
  const next = timeSlots[index + 1];
  return next && isSameDate(current.attributes.end, next.attributes.start)
    ? findLastAdjacent(index + 1, timeSlots)
    : index;
};

export const getAllTimeValues = (
  intl,
  timeZone,
  timeSlots,
  startDate,
  selectedStartTime,
  bookingLengthInMinutes,
  startTimeInterval,
  seatsEnabled
) => {
  if (timeSlots === undefined) {
    return [];
  }

  const startTimes = selectedStartTime
    ? []
    : getAvailableStartTimes({
      intl,
      timeZone,
      bookingStart: startDate,
      timeSlotsOnSelectedDate: getTimeSlotsOnDate(timeSlots, startDate, timeZone),
      bookingLengthInMinutes,
      startTimeInterval,
    });

  // Value selectedStartTime is a string when user has selected it through the form.
  // That's why we need to convert also the timestamp we use as a default
  // value to string for consistency. This is expected later when we
  // want to compare the sartTime and endTime.
  const startTime = selectedStartTime
    ? selectedStartTime
    : startTimes.length > 0 && startTimes[0] && startTimes[0].timestamp
      ? startTimes[0].timestamp.toString()
      : null;

  const startTimeAsDate = startTime ? timestampToDate(startTime) : null;
  const endTimeAsDate = startTimeAsDate
    ? getBookingEndTimeAsDate(startTimeAsDate, bookingLengthInMinutes)
    : null;

  const selectedTimeSlotIndex = timeSlots.findIndex(t =>
    isInRange(startTimeAsDate, t.attributes.start, t.attributes.end)
  );

  const selectedTimeSlot =
    selectedTimeSlotIndex >= 0 ? timeSlots[selectedTimeSlotIndex] : undefined;

  // findLastAdjacent is defined at the top of the file

  const findFirstAdjacent = index => {
    const current = timeSlots[index];
    const previous = timeSlots[index - 1];
    return previous && isSameDate(current.attributes.start, previous.attributes.end)
      ? findFirstAdjacent(index - 1)
      : index;
  };

  /**
   * Finds the smallest number of seats in time slots that meet the specified conditions.
   */
  const findMinimumAvailableSeats = (
    selectedEndTimeAsDateObject,
    timeSlots,
    selectedTimeSlotIndex
  ) => {
    // Retrieve the selected time slot from the list.
    const selectedTimeSlot = timeSlots[selectedTimeSlotIndex];
    if (!selectedTimeSlot) {
      return null; // Return null if the selected time slot is invalid.
    }

    // Check if the selected end time falls within the selected time slot.
    const endTimeIsWithinSelected = isInRange(
      selectedEndTimeAsDateObject - 1,
      selectedTimeSlot.attributes.start,
      selectedTimeSlot.attributes.end
    );

    if (endTimeIsWithinSelected) {
      return selectedTimeSlot.attributes.seats; // Return the seats for the selected time slot if end time and start time are within the same timeslot.
    }

    const lastIndex = findLastAdjacent(selectedTimeSlotIndex, timeSlots);

    // Extract the relevant time slots to check (we choose all slots between the first )
    const relevantTimeSlots = timeSlots.slice(selectedTimeSlotIndex, lastIndex + 1);

    // Find the smallest number of seats in the relevant time slots.
    const minSeats = relevantTimeSlots.reduce((smallest, timeslot) => {
      const seats = timeslot.attributes.seats;

      // Update the smallest seats found so far.
      const newSmallest = Math.min(smallest, seats);

      return newSmallest;
    }, 100); // Max seats value is 100

    return minSeats;
  };

  const combineTimeSlots = (currentTimeSlotIndex, timeSlots, seatsEnabled) => {
    if (currentTimeSlotIndex < 0 || !timeSlots || timeSlots.length === 0) {
      return null;
    }

    if (timeSlots.length === 1 || seatsEnabled === false) {
      return timeSlots[0];
    }
    const lastIndex = findLastAdjacent(currentTimeSlotIndex, timeSlots);
    const firstIndex = findFirstAdjacent(currentTimeSlotIndex, timeSlots);

    const smallestSeats = seatsEnabled
      ? findMinimumAvailableSeats(endTimeAsDate, timeSlots, currentTimeSlotIndex)
      : 1;

    const combinedTimeSlot = {
      ...timeSlots[currentTimeSlotIndex],
      attributes: {
        ...timeSlots[currentTimeSlotIndex].attributes,
        start: timeSlots[firstIndex].attributes.start,
        end: timeSlots[lastIndex].attributes.end,
        seats: smallestSeats,
      },
    };

    return combinedTimeSlot;
  };

  const combinedTimeSlot = combineTimeSlots(selectedTimeSlotIndex, timeSlots, seatsEnabled) || {};

  const endTime = endTimeAsDate?.getTime().toString();
  const finalTimeSlots = seatsEnabled ? combinedTimeSlot : selectedTimeSlot;

  return { startTime, endTime, selectedTimeSlot: finalTimeSlots, startTimes };
};
